import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { getAuth } from 'firebase/auth';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import {
  businessIntakeDocuments,
  getRequiredBusinessDocuments,
  getConditionalBusinessDocuments
} from '../../config/intakeDocumentRequirements';

const BusinessIntakeDocuments = ({ prequalId, brokerId, businessName }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [conditions, setConditions] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch existing uploaded intake documents
  useEffect(() => {
    const fetchUploadedDocs = async () => {
      if (!prequalId) return;

      try {
        const q = query(
          collection(db, 'prequalificationMetadata'),
          where('applicationId', '==', prequalId),
          where('documentType', '==', 'intake')
        );
        const querySnapshot = await getDocs(q);
        const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUploadedDocs(docs);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching intake documents:', error);
        setIsLoading(false);
      }
    };

    fetchUploadedDocs();
  }, [prequalId]);

  const handleConditionChange = (conditionKey) => {
    setConditions(prev => ({
      ...prev,
      [conditionKey]: !prev[conditionKey]
    }));
  };

  const isDocumentUploaded = (documentName, label) => {
    return uploadedDocs.some(doc =>
      doc.documentName === documentName &&
      doc.label === label &&
      doc.documentType === 'intake'
    );
  };

  const getUploadedDoc = (documentName, label) => {
    return uploadedDocs.find(doc =>
      doc.documentName === documentName &&
      doc.label === label &&
      doc.documentType === 'intake'
    );
  };

  const handleFileUpload = async (event, documentName, label) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!prequalId || !brokerId) {
      alert('Missing prequalification ID or broker ID. Please try again.');
      return;
    }

    const uploadKey = `${documentName}-${label}`;
    setUploadingStatus(prev => ({ ...prev, [uploadKey]: 'uploading' }));

    try {
      // Check if a file already exists for this document and label
      const existingDoc = getUploadedDoc(documentName, label);

      // If a file exists, delete it from storage and Firestore
      if (existingDoc) {
        try {
          const oldStorageRef = ref(storage, existingDoc.fileUrl);
          await deleteObject(oldStorageRef);
        } catch (e) {
          console.log('Could not delete old file from storage:', e);
        }
        await deleteDoc(doc(db, 'prequalificationMetadata', existingDoc.id));
      }

      // Upload new file to intake folder
      const storagePath = `prequalifications/${brokerId}/${prequalId}/intake/${documentName}/${label}/${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      // Save metadata to Firestore
      const fileMetadata = {
        documentName,
        label,
        fileName: file.name,
        fileUrl,
        uploadedAt: new Date().toISOString(),
        applicationId: prequalId,
        brokerId,
        businessName: businessName || '',
        documentType: 'intake',
        status: 'uploaded'
      };

      const docRef = await addDoc(collection(db, 'prequalificationMetadata'), fileMetadata);

      // Update local state
      setUploadedDocs(prev => {
        const filtered = prev.filter(d => !(d.documentName === documentName && d.label === label));
        return [...filtered, { id: docRef.id, ...fileMetadata }];
      });

      setUploadingStatus(prev => ({ ...prev, [uploadKey]: 'completed' }));

      // Reset status after a delay
      setTimeout(() => {
        setUploadingStatus(prev => ({ ...prev, [uploadKey]: null }));
      }, 2000);

    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadingStatus(prev => ({ ...prev, [uploadKey]: 'error' }));
      alert('Error uploading file. Please try again.');
    }
  };

  const requiredDocs = getRequiredBusinessDocuments();
  const conditionalDocs = getConditionalBusinessDocuments();

  // Get applicable conditional docs based on selected conditions
  const applicableConditionalDocs = conditionalDocs.filter(
    doc => conditions[doc.conditionKey]
  );

  const renderDocumentUploadSlot = (document, label, index) => {
    const uploadKey = `${document.name}-${label}`;
    const isUploaded = isDocumentUploaded(document.name, label);
    const uploadedDoc = getUploadedDoc(document.name, label);
    const status = uploadingStatus[uploadKey];
    const inputId = `intake-${document.name.replace(/\s+/g, '-')}-${label.replace(/\s+/g, '-')}-${index}`;

    return (
      <div key={`${document.name}-${label}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {isUploaded ? (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            </div>
          ) : (
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
              <DocumentIcon className="w-5 h-5 text-gray-500" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{label}</p>
            {isUploaded && uploadedDoc && (
              <p className="text-xs text-gray-500 truncate">{uploadedDoc.fileName}</p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 ml-3">
          {status === 'uploading' ? (
            <div className="flex items-center gap-2 text-indigo-600">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm">Uploading...</span>
            </div>
          ) : status === 'completed' ? (
            <span className="text-sm text-green-600 font-medium">Uploaded!</span>
          ) : status === 'error' ? (
            <span className="text-sm text-red-600 font-medium">Error</span>
          ) : (
            <label
              htmlFor={inputId}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-colors ${
                isUploaded
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              <CloudArrowUpIcon className="w-4 h-4" />
              {isUploaded ? 'Replace' : 'Upload'}
              <input
                id={inputId}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                onChange={(e) => handleFileUpload(e, document.name, label)}
                className="hidden"
              />
            </label>
          )}
        </div>
      </div>
    );
  };

  const renderDocumentSection = (document, index) => {
    return (
      <div key={document.name} className="mb-6">
        <div className="flex items-start gap-2 mb-2">
          <h4 className="text-sm font-semibold text-gray-800">{document.name}</h4>
          {document.required && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
              Required
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mb-3">{document.description}</p>
        {document.validationNote && (
          <div className="flex items-start gap-2 mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
            <ExclamationCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">{document.validationNote}</p>
          </div>
        )}
        <div className="space-y-2">
          {document.labels.map((label, labelIndex) => renderDocumentUploadSlot(document, label, `${index}-${labelIndex}`))}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
        <div className="px-6 py-5 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-xl overflow-hidden border border-indigo-100">
      {/* Header */}
      <div className="px-6 py-5 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white">
        <div className="flex items-center">
          <DocumentIcon className="h-5 w-5 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold leading-7 text-gray-900">Intake Documents</h3>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 hover:text-gray-700 focus:outline-none transition-colors p-1 rounded-full hover:bg-gray-100"
        >
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-indigo-100 px-6 py-5">
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
            <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800">
                Upload the following intake documents to complete the loan application package.
                Required documents are marked in red.
              </p>
            </div>
          </div>

          {/* Required Documents Section */}
          <div className="mb-8">
            <h3 className="text-md font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Required Documents
            </h3>
            {requiredDocs.map((doc, index) => renderDocumentSection(doc, index))}
          </div>

          {/* Conditional Documents Section */}
          <div>
            <h3 className="text-md font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Conditional Documents
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Check the boxes below if any of these situations apply to this transaction:
            </p>

            {/* Checkboxes for conditions */}
            <div className="space-y-3 mb-6">
              {conditionalDocs.map((doc) => (
                <label
                  key={doc.conditionKey}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={conditions[doc.conditionKey] || false}
                    onChange={() => handleConditionChange(doc.conditionKey)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">{doc.conditionLabel}</span>
                </label>
              ))}
            </div>

            {/* Conditional Document Upload Slots */}
            {applicableConditionalDocs.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-800 mb-4">
                  Additional Documents Required
                </h4>
                {applicableConditionalDocs.map((doc, index) => renderDocumentSection(doc, `conditional-${index}`))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessIntakeDocuments;
